import React from "react";
import { getByPath } from "../../utility/compassClientFilter";
import LoadingSpinner from "../../components/LoadingSpinner";

// Stable string key for a document _id, robust to the wire shape: a plain
// hex string (Convex / most Lambda responses) OR an Extended-JSON wrapper
// ({ $oid }). Used both for React keys AND selection matching, so the
// highlighted row survives a refetch that recreates the row objects.
function idKey(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$oid) return value.$oid;
  return String(value);
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — results grid (presentational, backend-agnostic).
//
// Renders a compact table: an always-present _id column plus the collection's
// catalog "preview" fields. Click a row to load it into the doc viewer. Big
// values (objects/arrays) are summarized so the grid stays scannable; the full
// value is in the doc viewer.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function formatCell(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return `[${value.length}]`;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    const keys = Object.keys(value);
    return `{${keys.length} field${keys.length === 1 ? "" : "s"}}`;
  }
  const s = String(value);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
}

function CompassResultsTable({
  docs,
  previewFields,
  loading,
  error,
  onSelect,
  selectedId,
}) {
  if (error) {
    return (
      <div className="text-red text-sm p-4">
        {typeof error === "string" ? error : "Error loading data."}
      </div>
    );
  }
  if (loading) {
    return <LoadingSpinner />;
  }
  if (!docs || docs.length === 0) {
    return (
      <div className="text-skin-link text-sm p-4 italic">
        No documents match.
      </div>
    );
  }

  // `previewFields` IS the full ordered column list (the catalog decides
  // whether _id appears — most collections drop it per Leland's spec).
  const cols = previewFields && previewFields.length ? previewFields : [{ label: "_id", path: "_id" }];

  const selKey = idKey(selectedId, null);

  return (
    <div className="overflow-auto h-full min-h-0">
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr className="text-left sticky top-0 bg-skin-primary">
            {cols.map((c) => (
              <th
                key={c.path}
                className="px-2 py-1 border-b-2 border-yellow whitespace-nowrap font-medium"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {docs.map((doc, idx) => {
            const id = idKey(doc._id, `row-${idx}`);
            const isSel = selKey != null && id === selKey;
            return (
              <tr
                key={id}
                onClick={() => onSelect && onSelect(doc)}
                className={`cursor-pointer ${isSel ? "font-medium" : "hover:bg-skin-base-hover"}`}
                style={
                  isSel
                    ? {
                        backgroundColor: "rgb(var(--color-button-accent))",
                        color: "#000",
                      }
                    : undefined
                }
              >
                {cols.map((c) => (
                  <td
                    key={c.path}
                    className="px-2 py-1 border-b border-dark-grey whitespace-nowrap"
                    title={String(getByPath(doc, c.path) ?? "")}
                  >
                    {formatCell(getByPath(doc, c.path))}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CompassResultsTable;
